/**
 * realtime/v1 — Central → Browser WebSocket (docs/architecture/05-events-and-realtime.md §4).
 * Endpoint: /api/realtime/v1. Short keys keep batches small; values are codes/numbers, never UI text.
 */
import { z } from 'zod';
import { Uuid } from '../../common.js';
export const REALTIME_CONTRACT = 'realtime/v1';
export const REALTIME_PATH = '/api/realtime/v1';
export const Topic = z.enum(['site', 'device', 'alarms', 'gateways', 'commands']);
// Client → server ---------------------------------------------------------------------------
export const ClientHello = z.object({
    t: z.literal('hello'),
    client_version: z.string().max(32),
    resume: z.object({ epoch: z.string().max(64), seq: z.number().int().nonnegative() }).nullable(),
});
export const ClientSubscribe = z.object({
    t: z.literal('sub'),
    id: z.string().min(1).max(32),
    topic: Topic,
    site_id: Uuid.optional(),
    device_id: Uuid.optional(),
    level: z.enum(['summary', 'detail']).default('summary'),
});
export const ClientUnsubscribe = z.object({ t: z.literal('unsub'), id: z.string().min(1).max(32) });
export const ClientPong = z.object({ t: z.literal('pong') });
export const ClientMessage = z.discriminatedUnion('t', [
    ClientHello,
    ClientSubscribe,
    ClientUnsubscribe,
    ClientPong,
]);
// Server → client ---------------------------------------------------------------------------
/**
 * Event kinds inside a batch:
 *  ds  = device state delta      pr  = device presence change   reg = device registry change
 *  al  = alarm change            cmd = command status change    gw  = gateway status/health
 */
export const EventKind = z.enum(['ds', 'pr', 'reg', 'al', 'cmd', 'gw']);
export const BatchEvent = z.object({
    k: EventKind,
    id: z.string().min(1).max(64),
    /** per-entity state_version; the client ignores patches with sv <= local */
    sv: z.number().int().nonnegative().optional(),
    p: z.record(z.string(), z.unknown()),
});
export const ServerWelcome = z.object({
    t: z.literal('welcome'),
    epoch: z.string().max(64),
    seq: z.number().int().nonnegative(),
    resumed: z.boolean(),
    server_version: z.string().max(32),
    min_client_version: z.string().max(32),
});
export const ServerBatch = z.object({
    t: z.literal('batch'),
    from: z.number().int().nonnegative(),
    to: z.number().int().nonnegative(),
    events: z.array(BatchEvent),
});
export const ServerResync = z.object({
    t: z.literal('resync'),
    sub_id: z.string().max(32).nullable(),
    reason: z.enum(['gap', 'epoch_changed', 'backpressure', 'server_restart']),
});
export const ServerSubOk = z.object({ t: z.literal('sub_ok'), id: z.string().max(32) });
export const ServerSubError = z.object({
    t: z.literal('sub_error'),
    id: z.string().max(32),
    code: z.enum(['FORBIDDEN', 'NOT_FOUND', 'INVALID', 'TOO_MANY']),
});
export const ServerPing = z.object({ t: z.literal('ping') });
export const ServerError = z.object({ t: z.literal('error'), code: z.string().max(64) });
export const ServerMessage = z.discriminatedUnion('t', [
    ServerWelcome,
    ServerBatch,
    ServerResync,
    ServerSubOk,
    ServerSubError,
    ServerPing,
    ServerError,
]);
//# sourceMappingURL=index.js.map